// Comments on a design round: threads (with replies) for one screen or the round as a whole.
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AttachmentList, RichTextarea, type RichTextareaHandle, type Uploaded } from './RichText';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api';
import { Menu, ago, initials, useConfirm, useToast } from '../ui';
import type { Comment, CommentList, RoundDetail } from '../types';

/** Where a round's comments live: the signed-in API, or the review-link API (/rapi). */
export interface CommentsApi {
  key: unknown[];          // the comment list's query key
  list: string;            // GET the list, POST a new comment
  item: (id: string) => string;
  refresh: unknown[][];    // other queries to refresh after a change (counts)
  upload?: string;         // where pasted images and files go
}

export const roundCommentsApi = (projectId: string, n: number): CommentsApi => ({
  key: ['comments', projectId, n],
  list: `/api/projects/${projectId}/rounds/${n}/comments`,
  item: (id) => `/api/comments/${id}`,
  refresh: [['rounds', projectId], ['round', projectId]],
  upload: `/api/projects/${projectId}/attachments`,
});

export function useComments(api: CommentsApi, enabled = true) {
  return useQuery({ queryKey: api.key, queryFn: () => get<CommentList>(api.list), enabled });
}

function useCommentMutations(api: CommentsApi) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => { for (const key of [api.key, ...api.refresh]) qc.invalidateQueries({ queryKey: key }); };
  const onError = (e: Error) => toast(e.message, true);
  return {
    create: useMutation({ mutationFn: (body: { screen_id?: string | null; parent_id?: string; body: string; internal?: boolean; attachment_ids?: string[] }) => post<Comment>(api.list, body), onSuccess: refresh, onError }),
    update: useMutation({ mutationFn: ({ id, ...body }: { id: string; body?: string; resolved?: boolean; attachment_ids?: string[] }) => patch<Comment>(api.item(id), body), onSuccess: refresh, onError }),
    remove: useMutation({ mutationFn: (id: string) => del(api.item(id)), onSuccess: refresh, onError }),
    toTicket: useMutation({ mutationFn: (id: string) => post<{ number: number }>(`/api/comments/${id}/ticket`, {}), onSuccess: (t) => { refresh(); qc.invalidateQueries({ queryKey: ['board'] }); qc.invalidateQueries({ queryKey: ['round-tickets'] }); toast(`Ticket #${t.number} created`); }, onError }),
    toIdea: useMutation({ mutationFn: (id: string) => post(`/api/comments/${id}/idea`), onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ['ideas'] }); toast('Added as an idea for the next round'); }, onError }),
  };
}

function Composer({ placeholder, list, onSubmit, autoFocus, onCancel, initial = '', submitLabel = 'Comment', allowInternal = true, upload }: {
  placeholder: string; list: CommentList; onSubmit: (body: string, internal: boolean, attachmentIds: string[]) => Promise<unknown>; autoFocus?: boolean;
  onCancel?: () => void; initial?: string; submitLabel?: string; allowInternal?: boolean; upload?: string;
}) {
  const [text, setText] = useState(initial);
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<Uploaded[]>([]);
  const box = useRef<RichTextareaHandle>(null);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!text.trim() || busy || text.includes('![Uploading ')) return;
    setBusy(true);
    try { await onSubmit(text, internal, files.map((f) => f.id)); box.current?.reset(); setInternal(false); } catch { /* toast shown */ } finally { setBusy(false); }
  };
  const showInternal = allowInternal && list.can_comment_internal && !list.internal_only;
  return (
    <form className={`composer ${internal ? 'internal' : ''}`} onSubmit={submit}>
      <RichTextarea ref={box} rows={text.length > 80 ? 4 : 2} value={text} onChange={setText} onFiles={setFiles} uploadUrl={upload}
                    placeholder={placeholder} maxLength={5000} autoFocus={autoFocus} onSubmit={() => submit()} onCancel={onCancel} />
      <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
        {showInternal ? (
          <label className="row tiny" title="Only Blue Market sees internal comments"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />Internal</label>
        ) : <span />}
        <div className="row" style={{ gap: 6 }}>
          {onCancel && <button type="button" className="btn sm ghost" onClick={onCancel}>Cancel</button>}
          <button className="btn sm primary" disabled={!text.trim() || busy || text.includes('![Uploading ')}>{busy && <span className="spin" />}{submitLabel}</button>
        </div>
      </div>
    </form>
  );
}

function CommentBody({ c, api, list, isReply }: { c: Comment; api: CommentsApi; list: CommentList; isReply?: boolean }) {
  const { update, remove } = useCommentMutations(api);
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(false);
  const items = [
    ...(c.can_edit ? [{ label: 'Edit', onClick: () => setEditing(true) }] : []),
    ...(c.can_delete ? [{ label: 'Delete', danger: true, onClick: async () => {
      if (await confirm('Delete this comment?', { body: isReply ? undefined : 'Replies stay, under a "deleted" placeholder.', ok: 'Delete', danger: true })) remove.mutate(c.id);
    } }] : []),
  ];
  return (
    <div className={`comment ${isReply ? 'reply' : ''}`}>
      <div className={`avatar sm ${c.author.is_staff ? 'staff' : ''}`} aria-hidden="true">{c.deleted ? '–' : initials(c.author.name)}</div>
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 4, alignItems: 'flex-start' }}>
          <div className="row wrap grow" style={{ gap: '2px 6px', minHeight: 26 }}>
            <b className="small">{c.deleted ? 'Deleted comment' : c.author.name}</b>
            {!c.deleted && c.author.is_staff && <span className="badge brand tiny-badge">Blue Market</span>}
            {!c.deleted && c.author.via_link && <span className="badge tiny-badge" title="Left using a review link">Review link</span>}
            {c.internal && !isReply && <span className="badge warn tiny-badge" title="Only Blue Market sees this">Internal</span>}
            {c.ticket && <Link to={c.ticket.url} className="badge info tiny-badge" title="This comment became a ticket">Ticket #{c.ticket.number}</Link>}
            {c.idea && <span className={`badge tiny-badge ${c.idea.status === 'done' ? 'ok' : 'info'}`} title="Turned into an idea for the next round (only Blue Market sees this)">
              {c.idea.status === 'done' ? 'Idea applied' : c.idea.status === 'dismissed' ? 'Idea dismissed' : 'Idea'}</span>}
            <span className="muted tiny" title={new Date(c.created_at).toLocaleString('en-ZA')}>{ago(c.created_at)}{c.edited ? ' · edited' : ''}</span>
          </div>
          {items.length > 0 && !editing && <Menu label="Comment actions" items={items} />}
        </div>
        {editing ? (
          <Composer list={list} placeholder="Edit your comment" initial={c.body} submitLabel="Save" autoFocus allowInternal={false} upload={api.upload}
                    onCancel={() => setEditing(false)} onSubmit={async (body, _i, attachment_ids) => { await update.mutateAsync({ id: c.id, body, attachment_ids }); setEditing(false); }} />
        ) : !c.deleted && <>
          <div className="comment-text rich-text" dangerouslySetInnerHTML={{ __html: c.body_html }} />
          <AttachmentList files={c.attachments ?? []} />
        </>}
      </div>
      {dialog}
    </div>
  );
}

function Thread({ c, replies, api, list }: { c: Comment; replies: Comment[]; api: CommentsApi; list: CommentList }) {
  const { create, update, toIdea, toTicket } = useCommentMutations(api);
  const [replying, setReplying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const collapsed = c.resolved && !expanded;
  return (
    <div className={`thread ${c.internal ? 'internal' : ''} ${c.resolved ? 'resolved' : ''}`}>
      {collapsed ? (
        <button className="thread-collapsed" onClick={() => setExpanded(true)}>
          <span className="badge ok tiny-badge">Resolved</span>
          <span className="muted small ellipsis">{c.deleted ? 'Deleted comment' : `${c.author.name}: ${c.body}`}</span>
          {replies.length > 0 && <span className="muted tiny">{replies.length} repl{replies.length === 1 ? 'y' : 'ies'}</span>}
        </button>
      ) : <>
        <CommentBody c={c} api={api} list={list} />
        {replies.map((r) => <CommentBody key={r.id} c={r} api={api} list={list} isReply />)}
        <div className="thread-actions">
          {list.can_comment && !replying && !(c.internal && !list.can_comment_internal) && <button className="btn sm ghost" onClick={() => setReplying(true)}>Reply</button>}
          {c.can_resolve && <button className="btn sm ghost" onClick={() => update.mutate({ id: c.id, resolved: !c.resolved })}>{c.resolved ? 'Reopen' : 'Resolve'}</button>}
          {list.can_make_tickets && !c.ticket && !c.deleted && <button className="btn sm ghost" onClick={() => toTicket.mutate(c.id)} disabled={toTicket.isPending}
                                                                    title="Creates a ticket linked to this screen">Make it a ticket</button>}
          {list.can_make_ideas && !c.idea && !c.deleted && <button className="btn sm ghost" onClick={() => toIdea.mutate(c.id)} disabled={toIdea.isPending}
                                                                  title="Adds it to the ideas for the next design round">Make it an idea</button>}
          {c.resolved && <button className="btn sm ghost" onClick={() => setExpanded(false)}>Collapse</button>}
        </div>
        {replying && (
          <div className="reply-box">
            <Composer list={list} placeholder="Reply" autoFocus allowInternal={false} submitLabel="Reply" onCancel={() => setReplying(false)} upload={api.upload}
                      onSubmit={async (body, _i, attachment_ids) => { await create.mutateAsync({ parent_id: c.id, body, attachment_ids }); setReplying(false); }} />
          </div>
        )}
      </>}
    </div>
  );
}

function closedMessage(round: RoundDetail): string {
  if (round.visibility === 'approved') return 'This round has been approved, so comments are closed.';
  return 'You can read the comments on this round.';
}

/** Threads for one screen (screenId) or for the round as a whole (screenId = null). */
export function CommentsPanel({ api, round, screenId, screenTitle, list, onJump }: {
  api: CommentsApi; round: RoundDetail; screenId: string | null; screenTitle?: string; list: CommentList | undefined;
  onJump?: (screenId: string) => void;
}) {
  const { create } = useCommentMutations(api);
  const { top, replies } = useMemo(() => {
    const all = list?.comments ?? [];
    const top = all.filter((c) => !c.parent_id && (c.screen_id ?? null) === screenId);
    top.sort((a, b) => Number(a.resolved) - Number(b.resolved) || a.created_at.localeCompare(b.created_at));
    const replies: Record<string, Comment[]> = {};
    for (const c of all) if (c.parent_id) (replies[c.parent_id] ??= []).push(c);
    return { top, replies };
  }, [list, screenId]);
  // On the overview: open threads on screens, to jump to.
  const elsewhere = useMemo(() => {
    if (screenId !== null || !list) return [];
    const titles = Object.fromEntries(round.screens.map((s) => [s.id, s.title]));
    const by: Record<string, number> = {};
    for (const c of list.comments) if (!c.parent_id && c.screen_id && !c.resolved && !c.deleted) by[c.screen_id] = (by[c.screen_id] ?? 0) + 1;
    return round.screens.filter((s) => by[s.id]).map((s) => ({ id: s.id, title: titles[s.id], count: by[s.id] }));
  }, [list, round, screenId]);
  if (!list) return <span className="spin muted" />;

  return (
    <div className="stack" style={{ gap: 14 }}>
      {list.internal_only && <p className="muted tiny" style={{ margin: 0 }}>This round is internal, so comments here are Blue Market only.</p>}
      {list.can_comment ? (
        <Composer list={list} placeholder={screenId ? `Comment on ${screenTitle ?? 'this screen'} (you can paste a screenshot)` : 'Comment on the round as a whole'}
                  upload={api.upload} onSubmit={(body, internal, attachment_ids) => create.mutateAsync({ screen_id: screenId, body, internal, attachment_ids })} />
      ) : <p className="muted small" style={{ margin: 0 }}>{closedMessage(round)}</p>}
      {top.map((c) => <Thread key={c.id} c={c} replies={replies[c.id] ?? []} api={api} list={list} />)}
      {!top.length && <p className="muted small" style={{ margin: 0 }}>{screenId ? 'No comments on this screen yet.' : 'No general comments yet.'}</p>}
      {elsewhere.length > 0 && (
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Open on screens</div>
          {elsewhere.map((s) => (
            <button key={s.id} className="jump" onClick={() => onJump?.(s.id)}><span className="grow ellipsis">{s.title}</span><span className="count">{s.count}</span></button>
          ))}
        </div>
      )}
    </div>
  );
}
